import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { api, type Layer, type DrillConfig, type DrillLevel } from '../lib/api';
import { legendItems, cleanPaintForMapLibre } from '../lib/paint';
import { BASE_MAPS, baseMapSource, safeBaseMapId, type BaseMapId } from '../lib/basemap';

function renderTemplate(tpl: string, props: Record<string, unknown>): string {
  return tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => {
    const v = props[k];
    return v == null ? '' : String(v);
  });
}

function computeBbox(g: any): [[number, number], [number, number]] | null {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  function visit(coords: any) {
    if (typeof coords[0] === 'number') {
      const [x, y] = coords;
      if (x < w) w = x; if (x > e) e = x;
      if (y < s) s = y; if (y > n) n = y;
    } else for (const c of coords) visit(c);
  }
  if (g.type === 'Point') visit(g.coordinates);
  else if (g.coordinates) visit(g.coordinates);
  else return null;
  if (!Number.isFinite(w)) return null;
  return [[w, s], [e, n]];
}

type GeoportalView = {
  slug: string;
  name: string;
  description: string | null;
  baseMap: BaseMapId;
  drillConfig?: DrillConfig | null;
  defaultView: { center: [number, number]; zoom: number; bearing: number; pitch: number };
};

// Una entrada del breadcrumb: el nivel de DOnde venimos y el valor que filtró al siguiente.
type DrillCrumb = { levelIdx: number; value: string; label: string };

export function Viewer() {
  const { slug } = useParams();
  const mapDiv = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  const [err, setErr]                 = useState<string | null>(null);
  const [gp, setGp]                   = useState<GeoportalView | null>(null);
  const [layers, setLayers]           = useState<(Layer & { tilesUrl: string })[]>([]);
  const [visible, setVisible]         = useState<Record<string, boolean>>({});
  const [baseMap, setBaseMap]         = useState<BaseMapId>('osm-positron');
  const [showLegend, setShowLegend]   = useState(true);
  const [showLayers, setShowLayers]   = useState(true);

  // Drill-down state: la "pila" desde la raíz hasta el nivel actual.
  // Vacío = mostrando nivel 0 (raíz).
  // Largo N = mostrando nivel N (filtrado por el padre N-1).
  const [drillStack, setDrillStack] = useState<DrillCrumb[]>([]);

  // ───────── carga + init map ─────────
  useEffect(() => {
    if (!slug || !mapDiv.current) return;

    api.publicGeoportal(slug).then(({ geoportal, layers }) => {
      const initialBase = safeBaseMapId(geoportal.baseMap);
      const drillConfig = geoportal.drillConfig ?? null;
      setGp({
        slug: geoportal.slug,
        name: geoportal.name,
        description: geoportal.description,
        baseMap: initialBase,
        drillConfig,
        defaultView: geoportal.defaultView,
      });
      setBaseMap(initialBase);
      setLayers(layers);

      // Visibilidad inicial: si drill activo, sólo el nivel 0 visible (resto oculto).
      const drillLayerIds = new Set(drillConfig?.enabled ? drillConfig.levels.map(l => l.layerId) : []);
      const rootDrillId = drillConfig?.enabled && drillConfig.levels[0] ? drillConfig.levels[0].layerId : null;
      const initVis: Record<string, boolean> = {};
      for (const l of layers) {
        if (drillLayerIds.has(l.id)) {
          initVis[l.id] = (l.id === rootDrillId);
        } else {
          initVis[l.id] = l.visibleDefault;
        }
      }
      setVisible(initVis);

      const sources: Record<string, maplibregl.SourceSpecification> = {};
      const mlLayers: maplibregl.LayerSpecification[] = [];

      const baseSrc = baseMapSource(initialBase);
      if (baseSrc) {
        sources['base'] = baseSrc;
        mlLayers.push({ id: 'base', type: 'raster', source: 'base' });
      }

      for (const l of layers) {
        const sourceId = `l_${l.id}`;
        const tilesUrl = location.origin + l.tilesUrl;
        sources[sourceId] = {
          type: 'vector',
          tiles: [tilesUrl],
          minzoom: l.minZoom,
          maxzoom: l.maxZoom,
        };
        const baseType = l.geometryType.includes('Polygon') ? 'fill' : l.geometryType.includes('Line') ? 'line' : 'circle';
        const visibility = initVis[l.id] ? 'visible' : 'none';
        mlLayers.push({
          id: sourceId,
          source: sourceId,
          'source-layer': 'mvt',
          type: baseType as any,
          paint: cleanPaintForMapLibre(l.paint as any) as any,
          layout: { visibility },
          minzoom: l.minZoom,
          maxzoom: l.maxZoom,
        });
        if (baseType === 'fill') {
          const outline = (l.paint as any)['fill-outline-color'];
          if (outline) {
            mlLayers.push({
              id: sourceId + '_outline',
              source: sourceId,
              'source-layer': 'mvt',
              type: 'line',
              paint: { 'line-color': outline, 'line-width': 1 },
              layout: { visibility },
              minzoom: l.minZoom,
              maxzoom: l.maxZoom,
            });
          }
        }
      }

      const map = new maplibregl.Map({
        container: mapDiv.current!,
        style: { version: 8, sources, layers: mlLayers },
        center: geoportal.defaultView.center,
        zoom: geoportal.defaultView.zoom,
        bearing: geoportal.defaultView.bearing ?? 0,
        pitch: geoportal.defaultView.pitch ?? 0,
      });
      mapRef.current = map;

      map.addControl(new maplibregl.NavigationControl(), 'top-right');
      map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

      class ResetView {
        _container?: HTMLDivElement;
        onAdd() {
          this._container = document.createElement('div');
          this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.title = 'Resetear vista';
          btn.style.cssText = 'font-size:14px;padding:0;width:29px;height:29px;cursor:pointer;background:white;border:none;';
          btn.innerHTML = '⌂';
          btn.onclick = () => {
            map.flyTo({
              center: geoportal.defaultView.center,
              zoom: geoportal.defaultView.zoom,
              bearing: geoportal.defaultView.bearing ?? 0,
              pitch: geoportal.defaultView.pitch ?? 0,
              duration: 600,
            });
          };
          this._container.appendChild(btn);
          return this._container;
        }
        onRemove() { this._container?.remove(); }
      }
      map.addControl(new ResetView() as any, 'top-right');

      // Click handler — necesita ser dinámico porque drillStack cambia.
      // Registramos un único listener que lee del state actual via cierres.
      map.on('click', e => handleMapClick(e));
      const allLayerIds = layers.map(l => `l_${l.id}`);
      map.on('mouseenter', allLayerIds, () => map.getCanvas().style.cursor = 'pointer');
      map.on('mouseleave', allLayerIds, () => map.getCanvas().style.cursor = '');

      // Refs internos para no recrear listener al cambiar state
      handlersRef.current = { drillConfig, layers };
    }).catch(e => setErr(String(e)));

    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, [slug]);

  // Refs vivos para usar dentro del click handler sin recrear el map
  const handlersRef = useRef<{ drillConfig: DrillConfig | null | undefined; layers: (Layer & { tilesUrl: string })[] }>({
    drillConfig: null, layers: [],
  });
  const drillStackRef = useRef<DrillCrumb[]>([]);
  useEffect(() => { drillStackRef.current = drillStack; }, [drillStack]);

  function handleMapClick(e: maplibregl.MapMouseEvent) {
    const map = mapRef.current; if (!map) return;
    const { drillConfig, layers } = handlersRef.current;
    const stack = drillStackRef.current;

    // Layer ids que aceptan click — sólo los visibles
    const visibleLayerIds = layers.filter(l => map.getLayer(`l_${l.id}`) && map.getLayoutProperty(`l_${l.id}`, 'visibility') !== 'none').map(l => `l_${l.id}`);
    const feats = map.queryRenderedFeatures(e.point, { layers: visibleLayerIds });
    if (!feats.length) return;
    const f = feats[0];
    const clickedLayerId = (f.layer.id as string).replace(/^l_/, '').replace(/_outline$/, '');
    const clickedLayer = layers.find(l => l.id === clickedLayerId);

    // ¿Es una capa drill? Si sí, intentamos bajar.
    let drilled = false;
    if (drillConfig?.enabled) {
      const currentLevelIdx = stack.length; // 0 si raíz, 1 después del primer click, ...
      const currentLevel: DrillLevel | undefined = drillConfig.levels[currentLevelIdx];
      const nextLevel:    DrillLevel | undefined = drillConfig.levels[currentLevelIdx + 1];
      if (currentLevel && nextLevel && currentLevel.layerId === clickedLayerId) {
        const id = (f.properties as any)?.[currentLevel.idColumn];
        if (id != null) {
          // Avanzar drill
          const label = (f.properties as any)?.nombre ?? String(id);
          const nextStack = [...stack, { levelIdx: currentLevelIdx, value: String(id), label: String(label) }];
          applyDrill(nextStack);
          drilled = true;
          // Zoom al feature clickeado
          try {
            const bbox = computeBbox(f.geometry as any);
            if (bbox) map.fitBounds(bbox, { padding: 60, maxZoom: 12, duration: 700 });
          } catch { /* ignore */ }
        }
      }
    }

    // Popup (independiente del drill — siempre se intenta)
    if (!drilled && clickedLayer?.popupTemplate) {
      const html = renderTemplate(clickedLayer.popupTemplate, f.properties as any);
      if (html) {
        new maplibregl.Popup({ closeOnMove: true, maxWidth: '320px' })
          .setLngLat(e.lngLat).setHTML(html).addTo(map);
      }
      try {
        const bbox = computeBbox(f.geometry as any);
        if (bbox) map.fitBounds(bbox, { padding: 60, maxZoom: 12, duration: 700 });
      } catch { /* ignore */ }
    }
  }

  // Aplica un nuevo stack de drill al mapa: oculta capas drill que no corresponden,
  // muestra la del nivel actual con filtro por parent.
  function applyDrill(nextStack: DrillCrumb[]) {
    const map = mapRef.current;
    const { drillConfig } = handlersRef.current;
    if (!map || !drillConfig?.enabled) return;
    setDrillStack(nextStack);

    const currentLevelIdx = nextStack.length; // luego de drill, mostramos este nivel
    const allDrillLayerIds = drillConfig.levels.map(l => l.layerId);
    const setVis = (lid: string, on: boolean) => {
      const fillId = `l_${lid}`;
      const lineId = `${fillId}_outline`;
      const v = on ? 'visible' : 'none';
      if (map.getLayer(fillId)) map.setLayoutProperty(fillId, 'visibility', v);
      if (map.getLayer(lineId)) map.setLayoutProperty(lineId, 'visibility', v);
    };

    // Ocultar todas las capas drill
    for (const lid of allDrillLayerIds) setVis(lid, false);

    // Mostrar la del nivel actual con filtro al padre inmediato
    const currentLevel = drillConfig.levels[currentLevelIdx];
    if (currentLevel) {
      setVis(currentLevel.layerId, true);
      // Filtro: parent_column == valor del último crumb
      if (nextStack.length > 0 && currentLevel.parentColumn) {
        const parent = nextStack[nextStack.length - 1];
        const filterExpr = ['==', ['to-string', ['get', currentLevel.parentColumn]], parent.value] as any;
        const fillId = `l_${currentLevel.layerId}`;
        if (map.getLayer(fillId))               map.setFilter(fillId, filterExpr);
        if (map.getLayer(fillId + '_outline'))  map.setFilter(fillId + '_outline', filterExpr);
      } else {
        const fillId = `l_${currentLevel.layerId}`;
        if (map.getLayer(fillId))               map.setFilter(fillId, null as any);
        if (map.getLayer(fillId + '_outline'))  map.setFilter(fillId + '_outline', null as any);
      }
    }

    // Sincronizar visibilidad de drillStack en el state local (para los toggles)
    setVisible(v => {
      const next = { ...v };
      for (const lid of allDrillLayerIds) next[lid] = (currentLevel?.layerId === lid);
      return next;
    });
  }

  function drillBack(toLevelIdx: number) {
    const newStack = drillStack.slice(0, toLevelIdx);
    applyDrill(newStack);
    if (gp) {
      const map = mapRef.current;
      if (map) map.flyTo({
        center: gp.defaultView.center,
        zoom: gp.defaultView.zoom,
        duration: 600,
      });
    }
  }

  function toggleLayer(id: string) {
    const map = mapRef.current; if (!map) return;
    const next = !visible[id];
    setVisible(v => ({ ...v, [id]: next }));
    const vis = next ? 'visible' : 'none';
    if (map.getLayer(`l_${id}`)) map.setLayoutProperty(`l_${id}`, 'visibility', vis);
    if (map.getLayer(`l_${id}_outline`)) map.setLayoutProperty(`l_${id}_outline`, 'visibility', vis);
  }

  function changeBaseMap(id: BaseMapId) {
    const map = mapRef.current; if (!map) return;
    setBaseMap(id);
    if (map.getLayer('base')) map.removeLayer('base');
    if (map.getSource('base')) map.removeSource('base');
    const src = baseMapSource(id);
    if (src) {
      map.addSource('base', src);
      const firstCustom = layers[0] ? `l_${layers[0].id}` : undefined;
      map.addLayer({ id: 'base', type: 'raster', source: 'base' }, firstCustom);
    }
  }

  if (err) return <div style={{ padding: 24 }}>{err}</div>;
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <div ref={mapDiv} style={{ position: 'absolute', inset: 0 }} />

      {gp && (
        <div style={hdrStyle}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{gp.name}</div>
          {gp.description && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{gp.description}</div>}
        </div>
      )}

      {/* Breadcrumb de drill-down */}
      {gp?.drillConfig?.enabled && (
        <div style={breadcrumbStyle}>
          <button onClick={() => drillBack(0)} style={crumbBtn(drillStack.length === 0)}>
            🏠 {gp.drillConfig.levels[0]?.label ?? 'Inicio'}
          </button>
          {drillStack.map((cr, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: '#94a3b8' }}>›</span>
              <button onClick={() => drillBack(i + 1)} style={crumbBtn(i === drillStack.length - 1)}>
                {cr.label}
              </button>
            </span>
          ))}
          {drillStack.length < (gp.drillConfig.levels.length - 1) && drillStack.length > 0 && (
            <span style={{ color: '#64748b', fontSize: 11, marginLeft: 4 }}>
              (click en {gp.drillConfig.levels[drillStack.length]?.label ?? 'feature'} para bajar)
            </span>
          )}
        </div>
      )}

      <div style={baseSelStyle}>
        <label style={{ fontSize: 10, textTransform: 'uppercase', color: '#64748b', display: 'block', marginBottom: 4 }}>Mapa base</label>
        <select value={baseMap} onChange={e => changeBaseMap(e.target.value as BaseMapId)}
          style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 4, background: 'white', minWidth: 130 }}>
          {BASE_MAPS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
        </select>
      </div>

      {layers.length > 0 && (
        <div style={{ ...panelStyle, top: 80, right: 12, maxWidth: 240 }}>
          <PanelHeader title="Capas" open={showLayers} onToggle={() => setShowLayers(s => !s)} />
          {showLayers && layers.map(l => (
            <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 12 }}>
              <input type="checkbox" checked={!!visible[l.id]} onChange={() => toggleLayer(l.id)} />
              <span style={{ flex: 1 }}>{l.name}</span>
            </label>
          ))}
        </div>
      )}

      {layers.length > 0 && (
        <div style={{ ...panelStyle, bottom: 30, right: 12, maxWidth: 240 }}>
          <PanelHeader title="Leyenda" open={showLegend} onToggle={() => setShowLegend(s => !s)} />
          {showLegend && layers.filter(l => visible[l.id]).map(l => (
            <div key={l.id} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 2 }}>{l.name}</div>
              {legendItems(l).map((it, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                  <span style={{ width: 14, height: 14, background: it.color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }} />
                  <span style={{ color: '#475569' }}>{it.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PanelHeader({ title, open, onToggle }: { title: string; open: boolean; onToggle: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: open ? 6 : 0 }} onClick={onToggle}>
      <span style={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', color: '#64748b' }}>{title}</span>
      <span style={{ fontSize: 14, color: '#94a3b8', userSelect: 'none' }}>{open ? '−' : '+'}</span>
    </div>
  );
}

const hdrStyle: React.CSSProperties = {
  position: 'absolute', top: 12, left: 12, background: 'rgba(255,255,255,0.95)',
  padding: '8px 12px', borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.1)', zIndex: 1, maxWidth: 320,
};
const breadcrumbStyle: React.CSSProperties = {
  position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
  background: 'rgba(255,255,255,0.95)', padding: '6px 10px', borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  zIndex: 2, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', maxWidth: '60%',
};
const crumbBtn = (active: boolean): React.CSSProperties => ({
  background: active ? '#0ea5e9' : 'transparent',
  color:      active ? 'white' : '#0369a1',
  border: 'none', padding: '3px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400,
});
const baseSelStyle: React.CSSProperties = {
  position: 'absolute', top: 12, right: 56, background: 'rgba(255,255,255,0.95)',
  padding: '6px 8px', borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.1)', zIndex: 1,
};
const panelStyle: React.CSSProperties = {
  position: 'absolute', background: 'rgba(255,255,255,0.95)', padding: 10, borderRadius: 6,
  fontSize: 12, boxShadow: '0 2px 6px rgba(0,0,0,0.1)', zIndex: 1,
};
